/**
 * Push notification registration utility.
 * Uses @capacitor-firebase/messaging which initializes the native Firebase SDK,
 * bridges APNs -> FCM on iOS, and returns true FCM tokens on both platforms.
 * Stores the FCM token in the backend via PUT /api/users/fcm-token.
 */

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Track whether listeners are already attached so foreground re-registration
// doesn't stack duplicate handlers.
let listenersAttached = false;

async function postToken(getIdToken, token) {
  if (!token) return;
  try {
    const idToken = await getIdToken();
    await axios.put(
      `${API}/users/fcm-token`,
      { fcm_token: token },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
  } catch (err) {
    console.error("Failed to store FCM token:", err);
  }
}

export async function registerPushNotifications(getIdToken) {
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isNativePlatform()) return;

    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

    const permResult = await FirebaseMessaging.requestPermissions();
    if (permResult.receive !== "granted") return;

    // On iOS this returns the FCM token (plugin bridges APNs -> FCM natively).
    try {
      const { token } = await FirebaseMessaging.getToken();
      await postToken(getIdToken, token);
    } catch (err) {
      // iOS may return the token asynchronously via the tokenReceived listener.
      console.error("getToken failed (will rely on tokenReceived):", err);
    }

    if (!listenersAttached) {
      // iOS APNs -> FCM async return + token rotation on both platforms.
      FirebaseMessaging.addListener("tokenReceived", async (event) => {
        await postToken(getIdToken, event?.token);
      });

      // Foreground delivery (so notifications surface while the app is open).
      FirebaseMessaging.addListener("notificationReceived", (event) => {
        console.log("Push notification received (foreground):", event?.notification);
      });

      listenersAttached = true;
    }
  } catch {
    // Not on native platform or plugin not available — silently ignore
  }
}
